import { Component, OnInit } from "@angular/core";
import { Location } from "@angular/common";

interface PrivacyItem {
    icon: string;
    title: string;
    description: string;
    color?: string; // 👈 optional
}
interface PrivacyContact {
    icon: string;
    title: string;
    email: string;
    note?: string;
}

interface PrivacySection {
    icon: string;
    title: string;
    subtitle?: string;
    description?: string;
    highlight?: string;
    variant?: string;
    items?: PrivacyItem[];
    warning?: string;
    checklistTitle?: string;
    checklist?: string[];
    contact?: PrivacyContact;
    contactInfo?: ContactInfo;
}

interface ContactRow {
    icon: string;
    label: string;
    value: string;
    isLink?: boolean;
    href?: string;
}

interface ContactInfo {
    company: string;
    rows: ContactRow[];
    footerNote: string;
}

@Component({
    selector: "app-privacy-page",
    templateUrl: "./privacy-page.component.html",
    styleUrls: ["./privacy-page.component.scss"],
    standalone: false,
})
export class PrivacyPageComponent {
    constructor(private location: Location) {}
    public email = "contact@udmglobal.com";

    public goBack() {
        this.location.back();
    }

    public privacySections: PrivacySection[] = [
        {
            variant: "default",
            icon: "database",
            title: "Personal Information We Collect",
            subtitle: "What data we gather and why it matters",
            description:
                "To provide you with a personalized and seamless shopping experience, we collect various types of personal information.Thos data helps us serve you better ,process your order efficiently improve our sevices",
            items: [
                {
                    icon: "users",
                    title: "Personalization & Account Information",
                    description:
                        "We collect your name, email address, phone number, date of birth, and profile preferences.",
                },
                {
                    icon: "vehicle",
                    title: "Order Handling & Delivery",
                    description: "Your shipping address, billing address, and delivery preferences are essential.",
                },
                {
                    icon: "creditcard",
                    title: "Payment Processing",
                    description: "We work with secure third-party payment gateways to process transactions.",
                },
                {
                    icon: "message",
                    title: "Communication & Support",
                    description: "Your communication preferences and support tickets help us assist you.",
                },
                {
                    icon: "sheilds",
                    title: "Fraud Prevention & Security",
                    description: "We collect device information and IP addresses to prevent fraud.",
                },
                {
                    icon: "chart",
                    title: "Service Improvement & Analytics",
                    description: "Usage data and feedback help us improve our services.",
                },
            ],
        },
        {
            variant: "blue",
            icon: "download",
            title: "How We Collect Your Information",
            subtitle: "The methods we use to gather data",
            description:
                "We collect information through various channels to ensure a comprehensive and personalized experience on FunFigz. Understanding how we gather this data helps you make informed decisions about your privacy.",
            items: [
                {
                    icon: "edit",
                    title: "Direct Input",
                    description: "When you create an account, place orders, or contact customer support.",
                },
                {
                    icon: "store",
                    title: "Seller Interactions",
                    description: "When you purchase products from third-party sellers on our platform.",
                },
                {
                    icon: "payment",
                    title: "Payment Gateways",
                    description: "Transaction information collected during checkout.",
                },
                {
                    icon: "communication",
                    title: "Communications",
                    description: "Emails, chat messages, phone calls, and feedback forms.",
                },
                {
                    icon: "fraud",
                    title: "Fraud Detection",
                    description: "We collect browsing patterns to identify suspicious activities.",
                },
                {
                    icon: "settings",
                    title: "Third-Party Technical Services",
                    description: "Analytics and marketing tools that help optimize performance.",
                },
            ],
        },
        {
            icon: "questionmark",
            title: "Why Do We Need Your Information",
            subtitle: "How we use your data to serve you better",
            description:
                "Your information enables us to provide exceptional service and create trustworthy marketplace.Here how we use data we collect",
            variant: "information",
            items: [
                {
                    icon: "platform",
                    title: "Platform Services",
                    description:
                        "To operate and maintain our platform, process transactions, and deliver core functionality.",
                },
                {
                    icon: "account",
                    title: "Account Management",
                    description: "To create, maintain, and secure your account and provide personalized settings.",
                },
                {
                    icon: "service",
                    title: "Service Improvements",
                    description: "To analyze usage patterns and improve platform performance.",
                },
                {
                    icon: "payment",
                    title: "Payment Processing",
                    description: "To securely process payments, verify transactions, and handle refunds.",
                },
                {
                    icon: "bell",
                    title: "Notices & Communication",
                    description: "To send confirmations, updates, and important announcements.",
                },
                {
                    icon: "support",
                    title: "Queries & Support",
                    description: "To respond to inquiries and provide technical assistance.",
                },
                {
                    icon: "dispute",
                    title: "Dispute Resolution",
                    description: "To investigate claims and facilitate fair resolutions.",
                },
                {
                    icon: "froudPrevent",
                    title: "Fraud Prevention",
                    description: "To detect suspicious activities and protect users.",
                },
                {
                    icon: "legal",
                    title: "Legal Enforcement",
                    description: "To comply with legal obligations and protect our rights.",
                },
            ],
        },
        {
            icon: "share",
            title: "Sharing of Your Information",
            subtitle: "When and how we share your data",
            highlight: "We do NOT sell or rent your personal data to third parties for marketing purposes.",
            description:
                "Your privacy is our priority. However, there are specific circumstances where we may share your information to provide our services effectively and comply with legal requirements:",
            items: [
                {
                    icon: "dispute",
                    title: "Third-Party Service Providers",
                    description:
                        "We share information with trusted service providers who assist us in operating our platform, processing payments, delivering products, sending communications, and analyzing data.",
                },
                {
                    icon: "payment",
                    title: "Payment Service Providers",
                    description:
                        "To process transactions securely, we share necessary payment information with authorized payment gateways and financial institutions.",
                },
                {
                    icon: "legal",
                    title: "Legal Disclosures",
                    description:
                        "We may disclose your information to government authorities or legal entities when required by law or court orders.",
                },
                {
                    icon: "enforcement",
                    title: "Enforcement of Terms",
                    description:
                        "Information may be shared to enforce our Terms of Service, investigate violations, or address security concerns.",
                },
                {
                    icon: "bell",
                    title: "User Notification",
                    description:
                        "Whenever legally permissible, we will notify you when your information may be shared with third parties.",
                },
            ],
        },
        {
            icon: "filters",
            title: "Choices You Can Make",
            subtitle: "Your control over your personal information",
            description:
                "We believe in empowering you with control over your personal information. You have several choices regarding how your data is collected and used:",
            variant: "choice",
            items: [
                {
                    icon: "hand",
                    title: "Voluntary Disclosure",
                    description:
                        "Providing personal information on FunFigz is entirely voluntary. You can choose what information to share with us. However, certain information may be necessary to access specific features or complete transactions. Without this information, we may not be able to provide you with the full range of our services.",
                },
                {
                    icon: "authenticity",
                    title: "Information Authenticity",
                    description:
                        "You are responsible for ensuring that the information you provide is accurate, complete, and up-to-date. FunFigz and UDM Global Solution Private Limited assume no liability for the authenticity or accuracy of information shared by users.Providing false or misleading information may result in account suspension or termination",
                },
                {
                    icon: "userdeletion",
                    title: "Account Deletion",
                    description:
                        "You have the right to delete your account and remove yourself from our platform at any time. To do so, please contact our support team at Please note that certain information may be retained for legal, regulatory, or legitimate business purposes even after account deletion ontact@udmglobal.com ",
                },
                {
                    icon: "communication",
                    title: "Communication Preferences",
                    description:
                        "You can manage your email and notification preferences in your account settings. While you can opt out of promotional communications, we may still send you essential service-related messages such as order confirmations and account updates.",
                },
            ],
        },
        {
            icon: "lock",
            title: "Threat to Your Information (Security)",
            subtitle: "How we protect your data from unauthorized access",
            description:
                "Protecting your personal information is our top priority. We implement robust security measures to safeguard your data from unauthorized access, disclosure, alteration, or destruction:",
            variant: "security",
            items: [
                {
                    icon: "ssl",
                    title: "SSL Encryption",
                    description:
                        "All data transmitted between your browser and our servers is encrypted using industry-standard SSL/TLS protocols, ensuring secure communication.",
                    color: "green",
                },
                {
                    icon: "authenticity",
                    title: "Security Measures",
                    description:
                        "We employ reasonable physical, electronic, and procedural safeguards to protect your information from unauthorized access and misuse.",
                    color: "blue",
                },
                {
                    icon: "authorizedaccess",
                    title: "Authorized Access",
                    description:
                        "Access to your personal information is restricted to authorized personnel only, based on a strict need-to-know basis for business operations.",
                    color: "sky",
                },
                {
                    icon: "training",
                    title: "Employee Training",
                    description:
                        "Our employees receive regular training on data protection best practices and are bound by confidentiality agreements.",
                    color: "yellow",
                },
                {
                    icon: "otp",
                    title: "OTP-Based Login",
                    description:
                        "We use One-Time Password (OTP) authentication to add an extra layer of security to your account login process.",
                    color: "pink",
                },
                {
                    icon: "passwordkey",
                    title: "No Password Storage",
                    description:
                        "We do not store your passwords or complete payment card details on our servers, minimizing the risk of data breaches.",
                    color: "cyan",
                },
                {
                    icon: "gateway",
                    title: "Secure Payment Gateway",
                    description:
                        "All payment transactions are processed through PCI-DSS compliant third-party payment gateways that maintain the highest security standards.",
                    color: "mint",
                },
            ],
            warning:
                "While we implement comprehensive security measures, no system is completely secure. We cannot guarantee absolute security of your information. You are responsible for maintaining the confidentiality of your account credentials and should notify us immediately of any unauthorized access.",
        },
        {
            icon: "headphone",
            title: "Grievances and Correction of Information",
            subtitle: "How to reach us for support and updates",
            description:
                "We are committed to addressing your concerns and ensuring the accuracy of your personal information. If you have any grievances, questions, or wish to update your data, we're here to help.",

            variant: "grievance",

            checklistTitle: "Contact Us For:",
            checklist: [
                "Reporting privacy concerns or grievances",
                "Updating or correcting your personal information",
                "Requesting access to your data",
                "Deleting your account",
                "Questions about this Privacy Policy",
            ],

            contact: {
                icon: "email",
                title: "Email Us",
                email: "contact@udmglobal.com",
                note: "Our team typically responds within 24–48 business hours",
            },

            warning:
                "We take all grievances seriously and will work diligently to resolve your concerns in a timely and satisfactory manner.",
        },
        {
            icon: "refresh",
            title: "Policy Updates",
            subtitle: "How we keep you informed about changes",
            description:
                "As our business evolves and legal requirements change, we may need to update this Privacy Policy from time to time. We are committed to keeping you informed about any changes.",
            variant: "policy",
            items: [
                {
                    icon: "penpaper",
                    title: "Right to Modify",
                    description:
                        "FunFigz and UDM Global Solution Private Limited reserve the right to change or update this Privacy Policy at any time to reflect changes in practices, technology, or legal requirements.",
                },
                {
                    icon: "power",
                    title: "Immediate Effect",
                    description:
                        "Any changes to this Privacy Policy will take effect immediately upon being posted on our website. The 'Last Updated' date will reflect the most recent changes.",
                },
                {
                    icon: "bell",
                    title: "Notification of Changes",
                    description:
                        "For significant changes that materially affect your rights, we will notify you via email or a prominent notice on our platform.",
                },
                {
                    icon: "userCheck",
                    title: "Continued Use",
                    description:
                        "By continuing to use FunFigz after changes are posted, you acknowledge and accept the updated terms.",
                },
            ],
        },
        {
            icon: "map",
            title: "Contact Information",
            subtitle: "Get in touch with us",
            variant: "contact",

            contactInfo: {
                company: "UDM Global Solution Private Limited",

                rows: [
                    {
                        icon: "building",
                        label: "Registered Office",
                        value:
                            "#55/3 & 55/4, 8th Floor\n" +
                            "Shilpitha Tech Park\n" +
                            "Devarabisanahalli, Bellandur\n" +
                            "Bangalore – 560103\n" +
                            "Karnataka, India",
                    },
                    {
                        icon: "phone",
                        label: "Phone",
                        value: "+91 7338617765",
                        isLink: true,
                        href: "tel:+917338617765",
                    },
                    {
                        icon: "email",
                        label: "Email",
                        value: "contact@udmglobal.com",
                        isLink: true,
                        href: "mailto:contact@udmglobal.com",
                    },
                ],

                footerNote: "Customer support available Monday to Saturday, 9:00 AM – 6:00 PM IST",
            },
        },
    ];
}
